import * as React from "react";
import {

  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";


const btnPrimarySx = {
  textTransform: "none",
  fontWeight: 900,
  bgcolor: "#111827", // dark
  color: "#fff",
  "&:hover": { bgcolor: "#0b1220" },
  borderRadius: 2,
};

const btnOutlineSx = {
  textTransform: "none",
  fontWeight: 900,
  color: "#111827",
  borderColor: "#111827",
  "&:hover": { borderColor: "#0b1220", bgcolor: "rgba(17,24,39,0.06)" },
  borderRadius: 2,
};

const BulletList = ({ items, iconColor = "success" }) => (
  <List dense sx={{ py: 0 }}>
    {items.map((t) => (
      <ListItem key={t} sx={{ px: 0, justifyContent: "center" }}>
        <ListItemIcon sx={{ minWidth: 34 }}>
          <CheckCircleRoundedIcon fontSize="small" color={iconColor} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ sx: { textAlign: "center" } }}
          primary={t}
        />
      </ListItem>
    ))}
  </List>
);

const CardBox = ({ title, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent sx={{ textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const Section = ({ id, icon, title, subtitle, children }) => (
  <Box id={id} sx={{ scrollMarginTop: 90, py: { xs: 4, md: 6 } }}>
    <Stack spacing={1} sx={{ mb: 2, alignItems: "center", textAlign: "center" }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        {icon}
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Stack>
      {subtitle ? (
        <Typography color="text.secondary" sx={{ maxWidth: 900 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
    {children}
  </Box>
);

export default function InvestorPageMUICentered() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      
      {/* Hero */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={3} alignItems="stretch" justifyContent="center">
            <Grid item xs={12} md={7}>
              <Stack alignItems="center" textAlign="center">
                <Typography variant="h3" sx={{ fontWeight: 900 }}>
                  We build a wedge product that becomes the system of record.
                </Typography>

                <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 760 }}>
                  Replace this with a crisp 1–2 sentence description: who you serve, what you do,
                  and the measurable outcome.
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2, justifyContent: "center" }}>
                  <Chip label="Stage: Seed" />
                  <Chip label="Model: SaaS" />
                  <Chip label="Target: Mid-market → Enterprise" />
                </Stack>

                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5, justifyContent: "center" }}>
                  <Button href="#market" variant="contained" size="large" sx={btnPrimarySx}>
                    View overview
                  </Button>
                  <Button href="#contact" variant="outlined" size="large" sx={btnOutlineSx}>
                    Contact
                  </Button>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 2.5,
                  height: "100%",
                  textAlign: "center",
                }}
              >
                <Stack spacing={1.5} alignItems="center">
                  <Stack direction="row" justifyContent="center" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 900 }}>Snapshot</Typography>
                    <Chip size="small" color="success" label="Live" />
                  </Stack>

                  <Grid container spacing={1.5}>
                    {[
                      ["TAM", "$X.XB"],
                      ["SAM", "$XXXM"],
                      ["SOM", "$XXM"],
                      ["MoM Growth", "X%"],
                    ].map(([k, v]) => (
                      <Grid item xs={6} key={k}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="overline" color="text.secondary">
                              {k}
                            </Typography>
                            <Typography sx={{ fontWeight: 900 }}>{v}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ width: "100%" }} />

                  <BulletList
                    items={[
                      "Pilot customers in progress",
                      "Clear ICP + buyer mapped",
                      "Wedge built, platform roadmap defined",
                    ]}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sections */}
      <Container maxWidth="lg">
        <Section
          id="market"
          icon={<TimelineRoundedIcon color="primary" />}
          title="Market Opportunity"
          subtitle="Size of the prize, why now, and wedge → expansion."
        >
          <Grid container spacing={2.5} justifyContent="center">
            <Grid item xs={12} md={6}>
              <CardBox title="Why now">
                <List dense>
                  {[
                    "Workflow shift (AI + automation + consolidation).",
                    "More demand for auditable systems.",
                    "Budgets moving from point tools → platforms.",
                  ].map((t) => (
                    <ListItem key={t} sx={{ px: 0, justifyContent: "center" }}>
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <RocketLaunchRoundedIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ sx: { textAlign: "center" } }}
                        primary={t}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardBox title="Market sizing">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                  <Chip label="TAM: $X.XB" />
                  <Chip label="SAM: $XXXM" />
                  <Chip label="SOM: $XXM" />
                  <Chip label="ICP: [role] at [company]" />
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">
                  <b>Wedge:</b> Solve one painful workflow first → expand into adjacent modules and higher ACV.
                </Typography>
              </CardBox>
            </Grid>
          </Grid>
        </Section>

        <Divider />

        <Section
          id="fit"
          icon={<VerifiedRoundedIcon color="primary" />}
          title="Problem–Solution Fit"
          subtitle="Clear pain, why alternatives fail, and why your approach wins."
        >
          <Grid container spacing={2.5} justifyContent="center">
            <Grid item xs={12} md={6}>
              <CardBox title="Problem">
                <BulletList
                  iconColor="action"
                  items={[
                    "Fragmented tools + manual processes.",
                    "Slow decisions due to scattered data.",
                    "Existing tools are rigid, expensive, or outdated.",
                  ]}
                />
              </CardBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <CardBox title="Solution">
                <BulletList
                  items={[
                    "Automates the workflow end-to-end.",
                    "Creates a single source of truth + audit trail.",
                    "Delivers measurable ROI within weeks.",
                  ]}
                />
              </CardBox>
            </Grid>
          </Grid>
        </Section>

        <Divider />

        <Section
          id="advantage"
          icon={<VerifiedRoundedIcon color="primary" />}
          title="Competitive Advantage"
          subtitle="Differentiation + defensibility: distribution, data, lock-in, cost, speed."
        >
          <Grid container spacing={2.5} justifyContent="center">
            <Grid item xs={12} md={7}>
              <CardBox title="Why we win">
                <BulletList
                  items={[
                    "Workflow-first: integrates into existing tools.",
                    "Faster time-to-value than legacy platforms.",
                    "Compounding moat via usage/data loops.",
                    "Expansion path increases ACV over time.",
                  ]}
                />
              </CardBox>
            </Grid>
            <Grid item xs={12} md={5}>
              <CardBox title="Competitive set">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                  <Chip label="Legacy suites" variant="outlined" />
                  <Chip label="Point tools" variant="outlined" />
                  <Chip label="Spreadsheets" variant="outlined" />
                  <Chip label="Internal builds" variant="outlined" />
                </Stack>
              </CardBox>
            </Grid>
          </Grid>
        </Section>

        <Divider />

        <Section
          id="roadmap"
          icon={<TimelineRoundedIcon color="primary" />}
          title="Development Roadmap"
          subtitle="Milestones investors expect: product, traction, revenue, readiness."
        >
          <Grid container spacing={2.5} justifyContent="center">
            {[
              ["Now (0–3 months)", ["Finalize MVP", "3–5 design partners", "Define ICP + pricing"]],
              ["Next (3–9 months)", ["Convert pilots → paid", "Add key integrations", "Improve reporting"]],
              ["Later (9–18 months)", ["Expand modules", "Scale GTM motion", "Enterprise readiness"]],
            ].map(([title, items]) => (
              <Grid item xs={12} md={4} key={title}>
                <CardBox title={title}>
                  <BulletList iconColor="action" items={items} />
                </CardBox>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Divider />

        <Section
          id="funding"
          icon={<PaymentsRoundedIcon color="primary" />}
          title="Funding Philosophy"
          subtitle="How capital is used and what outcomes it unlocks."
        >
          <Grid container spacing={2.5} justifyContent="center">
            <Grid item xs={12} md={6}>
              <CardBox title="Use of funds">
                <BulletList
                  items={[
                    "Product + integrations + reliability",
                    "GTM experiments → repeatable motion",
                    "Hiring only after ROI is proven",
                    "Security/compliance for enterprise deals",
                  ]}
                />
              </CardBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <CardBox title="Principles">
                <BulletList
                  iconColor="action"
                  items={[
                    "Capital efficient scaling",
                    "Milestone-driven fundraising",
                    "Defensibility + expansion",
                    "Investors who help distribution + hiring",
                  ]}
                />
              </CardBox>
            </Grid>
          </Grid>
        </Section>

        <Divider />

        {/* Contact */}
        

          
      </Container>
    </Box>
  );
}
